import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Lock, Mail, User, ArrowRight, Sparkles, Eye, EyeOff, Radar, Video, Plane } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TECH_IMG = "https://images.unsplash.com/photo-1595134334453-46c042d486f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByb29tJTIwdGVjaHxlbnwwfHx8fDE3NzYzMTY3MjR8MA&ixlib=rb-4.1.0&q=85&w=1200";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { t } = useLanguage();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState("login"); // login | register | forgot | magic | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [magicToken, setMagicToken] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      const routes = { "super-admin": "/admin", admin: "/admin", operator: "/operator", client: "/client" };
      navigate(routes[user.role] || "/operator", { replace: true });
    }
  }, [user, navigate]);

  // Handle magic link or reset token from URL
  useEffect(() => {
    const mt = searchParams.get("magic_token");
    const rt = searchParams.get("reset_token");
    if (mt) { setMagicToken(mt); setMode("magic_verify"); }
    if (rt) { setResetToken(rt); setMode("reset"); }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      login(res.data);
      toast.success(t("auth.login_success"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) { setError(t("auth.passwords_mismatch")); return; }
    if (password.length < 8) { setError(t("auth.password_min")); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { name, email, password });
      login(res.data);
      toast.success(t("auth.register_success"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/magic-link`, { email });
      toast.success(t("auth.magic_sent"));
      setMode("magic_verify");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const handleMagicVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/magic-link/verify`, { token: magicToken });
      login(res.data);
      toast.success(t("auth.login_success"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success(t("auth.reset_sent"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError(t("auth.password_min")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, password: newPassword });
      toast.success(t("auth.password_reset_success"));
      setMode("login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Branding Panel */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-[#0A0A0A] flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0">
          <img src={TECH_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/60 to-[#0A0A0A]" />
        </div>
        <div className="relative z-10">
          <h1 className="text-2xl font-black text-white tracking-tight mb-1" data-testid="login-brand-logo">Talk2Support</h1>
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-bold">LLC</p>
        </div>
        <div className="relative z-10 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tighter leading-tight">
            {t("auth.brand_headline")}
          </h2>
          <p className="text-sm text-white/50 leading-relaxed max-w-md">
            {t("auth.brand_subline")}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Shield, label: "Hattrix Premier Cloud" },
              { icon: Radar, label: t("landing.tech_radar") },
              { icon: Video, label: t("landing.tech_video") },
              { icon: Plane, label: t("landing.tech_drone") },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2.5 p-2.5 border border-white/10 bg-white/5">
                <item.icon size={16} className="text-[#002FA7] shrink-0" />
                <span className="text-[11px] text-white/70 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <p className="text-[10px] text-white/30">&copy; 2026 Talk2Support LLC. Dominican Republic.</p>
        </div>
      </div>

      {/* Right: Auth Forms */}
      <div className="flex-1 flex flex-col min-h-screen bg-white">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <h1 className="text-base font-black text-[#0A0A0A] tracking-tight lg:hidden">Talk2Support</h1>
          <LanguageToggle />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <div className="w-full max-w-sm">
            {/* Mode: Login */}
            {mode === "login" && (
              <div className="animate-fade-in" data-testid="login-form">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight mb-1">{t("auth.sign_in")}</h3>
                <p className="text-sm text-[#9CA3AF] mb-6">{t("auth.sign_in_desc")}</p>

                {error && <div className="p-3 mb-4 bg-[#FF2A2A]/5 border border-[#FF2A2A]/20 text-xs text-[#FF2A2A] font-medium" data-testid="login-error">{error}</div>}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("common.email")}</Label>
                    <div className="relative mt-1">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                      <Input className="rounded-none pl-9 h-10" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("auth.password")}</Label>
                    <div className="relative mt-1">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                      <Input className="rounded-none pl-9 pr-9 h-10" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" disabled={loading} data-testid="login-submit">
                    {loading ? t("common.loading") : t("auth.sign_in")} {!loading && <ArrowRight size={14} className="ml-1" />}
                  </Button>
                </form>

                <div className="mt-4 space-y-2">
                  <button className="text-xs text-[#002FA7] font-semibold hover:underline" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-link">{t("auth.forgot_password")}</button>
                  <div className="flex items-center gap-2 my-3">
                    <div className="flex-1 h-px bg-[#E5E7EB]" />
                    <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">{t("auth.or")}</span>
                    <div className="flex-1 h-px bg-[#E5E7EB]" />
                  </div>
                  <Button variant="outline" className="rounded-none w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" onClick={() => { setMode("magic"); setError(""); }} data-testid="magic-link-btn">
                    <Sparkles size={14} className="mr-1" /> {t("auth.use_magic_link")}
                  </Button>
                </div>

                <p className="text-xs text-[#9CA3AF] mt-6 text-center">
                  {t("auth.no_account")} <button className="text-[#002FA7] font-semibold hover:underline" onClick={() => { setMode("register"); setError(""); }} data-testid="register-link">{t("auth.register_now")}</button>
                </p>
              </div>
            )}

            {/* Mode: Register */}
            {mode === "register" && (
              <div className="animate-fade-in" data-testid="register-form">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight mb-1">{t("auth.create_account")}</h3>
                <p className="text-sm text-[#9CA3AF] mb-6">{t("auth.register_desc")}</p>

                {error && <div className="p-3 mb-4 bg-[#FF2A2A]/5 border border-[#FF2A2A]/20 text-xs text-[#FF2A2A] font-medium" data-testid="register-error">{error}</div>}

                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("common.name")}</Label>
                    <div className="relative mt-1">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                      <Input className="rounded-none pl-9 h-10" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("common.email")}</Label>
                    <div className="relative mt-1">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                      <Input className="rounded-none pl-9 h-10" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("auth.password")}</Label>
                    <Input className="rounded-none h-10 mt-1" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password" />
                    <p className="text-[10px] text-[#9CA3AF] mt-1">{t("auth.password_min")}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("auth.confirm_password")}</Label>
                    <Input className="rounded-none h-10 mt-1" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="register-confirm" />
                  </div>
                  <Button type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" disabled={loading} data-testid="register-submit">
                    {loading ? t("common.loading") : t("auth.create_account")}
                  </Button>
                </form>
                <p className="text-xs text-[#9CA3AF] mt-4 text-center">
                  {t("auth.have_account")} <button className="text-[#002FA7] font-semibold hover:underline" onClick={() => { setMode("login"); setError(""); }} data-testid="back-to-login">{t("auth.sign_in")}</button>
                </p>
              </div>
            )}

            {/* Mode: Magic Link Request */}
            {mode === "magic" && (
              <div className="animate-fade-in" data-testid="magic-form">
                <h3 className="text-2xl font-bold text-[#0A0A0A] tracking-tight mb-1">{t("auth.magic_title")}</h3>
                <p className="text-sm text-[#9CA3AF] mb-6">{t("auth.magic_desc")}</p>
                {error && <div className="p-3 mb-4 bg-[#FF2A2A]/5 border border-[#FF2A2A]/20 text-xs text-[#FF2A2A] font-medium">{error}</div>}
                <form onSubmit={handleMagicLink} className="space-y-4">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("common.email")}</Label>
                    <Input className="rounded-none h-10 mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="magic-email" />
                  </div>
                  <Button type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" disabled={loading} data-testid="magic-submit">
                    {loading ? t("common.loading") : t("auth.send_magic_link")}
                  </Button>
                </form>
                <button className="text-xs text-[#002FA7] font-semibold hover:underline mt-4" onClick={() => { setMode("login"); setError(""); }}>{t("common.back")}</button>
              </div>
            )}

            {/* Mode: Magic Link Verify */}
            {mode === "magic_verify" && (
              <div className="animate-fade-in" data-testid="magic-verify-form">
                <h3 className="text-2xl font-bold text-[#0A0A0A] tracking-tight mb-1">{t("auth.verify_magic")}</h3>
                <p className="text-sm text-[#9CA3AF] mb-6">{t("auth.verify_magic_desc")}</p>
                {error && <div className="p-3 mb-4 bg-[#FF2A2A]/5 border border-[#FF2A2A]/20 text-xs text-[#FF2A2A] font-medium">{error}</div>}
                <form onSubmit={handleMagicVerify} className="space-y-4">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("auth.magic_token")}</Label>
                    <Input className="rounded-none h-10 mt-1 font-mono" value={magicToken} onChange={(e) => setMagicToken(e.target.value)} required data-testid="magic-token-input" />
                  </div>
                  <Button type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" disabled={loading} data-testid="magic-verify-submit">
                    {loading ? t("common.loading") : t("auth.verify")}
                  </Button>
                </form>
                <button className="text-xs text-[#002FA7] font-semibold hover:underline mt-4" onClick={() => { setMode("login"); setError(""); }}>{t("common.back")}</button>
              </div>
            )}

            {/* Mode: Forgot Password */}
            {mode === "forgot" && (
              <div className="animate-fade-in" data-testid="forgot-form">
                <h3 className="text-2xl font-bold text-[#0A0A0A] tracking-tight mb-1">{t("auth.forgot_title")}</h3>
                <p className="text-sm text-[#9CA3AF] mb-6">{t("auth.forgot_desc")}</p>
                {error && <div className="p-3 mb-4 bg-[#FF2A2A]/5 border border-[#FF2A2A]/20 text-xs text-[#FF2A2A] font-medium">{error}</div>}
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("common.email")}</Label>
                    <Input className="rounded-none h-10 mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" />
                  </div>
                  <Button type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" disabled={loading} data-testid="forgot-submit">
                    {loading ? t("common.loading") : t("auth.send_reset")}
                  </Button>
                </form>
                <button className="text-xs text-[#002FA7] font-semibold hover:underline mt-4" onClick={() => { setMode("login"); setError(""); }}>{t("common.back")}</button>
              </div>
            )}

            {/* Mode: Reset Password */}
            {mode === "reset" && (
              <div className="animate-fade-in" data-testid="reset-form">
                <h3 className="text-2xl font-bold text-[#0A0A0A] tracking-tight mb-1">{t("auth.reset_title")}</h3>
                <p className="text-sm text-[#9CA3AF] mb-6">{t("auth.reset_desc")}</p>
                {error && <div className="p-3 mb-4 bg-[#FF2A2A]/5 border border-[#FF2A2A]/20 text-xs text-[#FF2A2A] font-medium">{error}</div>}
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">{t("auth.new_password")}</Label>
                    <Input className="rounded-none h-10 mt-1" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="reset-password-input" />
                  </div>
                  <Button type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" disabled={loading} data-testid="reset-submit">
                    {loading ? t("common.loading") : t("auth.reset_password")}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
